import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  ListGroup,
  Badge,
  ButtonGroup
} from 'react-bootstrap';
import consultationService from '../../services/consultationService';
import { lookupMembersByMatricule } from '../../services/memberService';
import Icon from '../common/Icons';
import { ClinicalButton } from './ui';
import { HEAD_DOCTOR_COPY } from '../head-doctor/headDoctorCopy';
import { DOCTOR_COPY } from './doctorCopy';
import DiagnosisDiseaseField from './DiagnosisDiseaseField';
import ConsultationPatientIdentityPremium from './ConsultationPatientIdentityPremium';
import { buildDiagnosisApiPayload, parseDiseasesFromConsultation } from './utils/diagnosisDisease';
import { CONSULTATION_STATUS } from './utils/consultationStatus';
import {
  toDateTimeLocalValue,
  nowDateTimeLocalValue,
  fromDateTimeLocalValue
} from '../../utils/appTimezone';
import './ConsultationEditor.css';
import WorkAccidentConfirmModal from './WorkAccidentConfirmModal';
import './WorkAccidentConfirmModal.css';

/**
 * Formulaire consultation plein écran (pas de modal) — même logique que l’ancien ConsultationForm.
 * @param {() => void} onCancel Retour liste / fermer
 * @param {(payload: { consultation?: object, wasCreate: boolean }) => void} [onSuccess]
 * @typedef {{ saveConsultation: () => Promise<{ success: boolean, consultation?: object, wasCreate?: boolean, error?: string }> }} ConsultationEditorHandle
 */
const ConsultationEditor = forwardRef(function ConsultationEditor(
  {
    consultation = null,
    viewerSiteId = '',
    clinicalWorkspace = 'physician',
    showNursingVitals = false,
    afterExamenSection = null,
    identityFooterTarget = null,
    identityInPageFooter = false,
    readOnly = false,
    useFixedPageFooter = false,
    onFooterStateChange,
    onSuccess,
    onCancel = () => {}
  },
  ref
) {
  const captureNursingVitals = !!showNursingVitals;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [legacyMode, setLegacyMode] = useState(false);
  const [matriculeQuery, setMatriculeQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [showWorkAccidentModal, setShowWorkAccidentModal] = useState(false);
  const editorStateRef = useRef({});

  const [formData, setFormData] = useState({
    patient_name: '',
    patient_phone: '',
    patient_age: '',
    patient_gender: '',
    consultation_date: nowDateTimeLocalValue(),
    symptoms: '',
    diagnosis: '',
    notes: '',
    weight_kg: '',
    height_cm: '',
    bp_systolic: '',
    bp_diastolic: '',
    temperature_c: '',
    heart_rate_bpm: '',
    is_work_accident: false,
    work_accident_location: '',
    work_accident_severity: '',
    is_occupational_disease: false
  });

  const isNurse = clinicalWorkspace === 'nurse';
  const isDentist = clinicalWorkspace === 'dentist';
  const isHeadDoctor = clinicalWorkspace === 'headDoctor';
  /** Diagnostic = codes maladie (référentiel), y compris en infirmerie. */
  const useCodedDiagnosis = true;
  const isEdit = !!consultation;
  const formId = useMemo(
    () => `consultation-editor-form-${consultation?.id ?? 'new'}`,
    [consultation?.id]
  );
  const hdEditor = HEAD_DOCTOR_COPY.CONSULTATION_EDITOR;
  const workAccidentCopy = isHeadDoctor
    ? {
        label: hdEditor.WORK_ACCIDENT_LABEL,
        yes: hdEditor.WORK_ACCIDENT_YES,
        no: hdEditor.WORK_ACCIDENT_NO,
        hint: hdEditor.WORK_ACCIDENT_HINT,
        locationLabel: hdEditor.WORK_ACCIDENT_LOCATION_LABEL,
        locationWork: hdEditor.WORK_ACCIDENT_LOCATION_WORK,
        locationCommute: hdEditor.WORK_ACCIDENT_LOCATION_COMMUTE,
        severityLabel: hdEditor.WORK_ACCIDENT_SEVERITY_LABEL,
        severityAtg: hdEditor.WORK_ACCIDENT_SEVERITY_ATG,
        severityAtb: hdEditor.WORK_ACCIDENT_SEVERITY_ATB,
        modalTitle: hdEditor.WORK_ACCIDENT_MODAL_TITLE,
        modalIntro: hdEditor.WORK_ACCIDENT_MODAL_INTRO,
        modalConfirm: hdEditor.WORK_ACCIDENT_MODAL_CONFIRM,
        modalCancel: hdEditor.WORK_ACCIDENT_MODAL_CANCEL,
        summaryEdit: hdEditor.WORK_ACCIDENT_SUMMARY_EDIT,
        validationLocation: DOCTOR_COPY.WORK_ACCIDENT.VALIDATION_LOCATION,
        validationSeverity: DOCTOR_COPY.WORK_ACCIDENT.VALIDATION_SEVERITY
      }
    : DOCTOR_COPY.WORK_ACCIDENT;
  const occupationalDiseaseCopy = isHeadDoctor
    ? {
        label: hdEditor.OCCUPATIONAL_DISEASE_LABEL,
        yes: hdEditor.OCCUPATIONAL_DISEASE_YES,
        no: hdEditor.OCCUPATIONAL_DISEASE_NO,
        hint: hdEditor.OCCUPATIONAL_DISEASE_HINT
      }
    : DOCTOR_COPY.PROFESSIONAL_DISEASE;
  const showWorkAccidentContext = !isDentist;

  const workAccidentIsConfirmed =
    !!formData.is_work_accident &&
    !!formData.work_accident_location &&
    !!formData.work_accident_severity;

  const openWorkAccidentModal = useCallback(() => {
    if (readOnly || loading) return;
    setShowWorkAccidentModal(true);
  }, [readOnly, loading]);

  const closeWorkAccidentModal = useCallback(() => {
    setShowWorkAccidentModal(false);
  }, []);

  const confirmWorkAccident = useCallback(({ location, severity }) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        is_work_accident: true,
        work_accident_location: location,
        work_accident_severity: severity
      };
      editorStateRef.current = { ...editorStateRef.current, formData: next };
      return next;
    });
    setShowWorkAccidentModal(false);
  }, []);

  const clearWorkAccident = useCallback(() => {
    setFormData((prev) => {
      const next = {
        ...prev,
        is_work_accident: false,
        work_accident_location: '',
        work_accident_severity: ''
      };
      editorStateRef.current = { ...editorStateRef.current, formData: next };
      return next;
    });
    setShowWorkAccidentModal(false);
  }, []);

  const getWorkAccidentLocationLabel = useCallback(
    (location) => {
      if (location === 'WORK') {
        return workAccidentCopy.locationWork || workAccidentCopy.LOCATION_WORK;
      }
      if (location === 'COMMUTE') {
        return workAccidentCopy.locationCommute || workAccidentCopy.LOCATION_COMMUTE;
      }
      return '';
    },
    [workAccidentCopy]
  );

  const headerMeta = useMemo(() => {
    if (isHeadDoctor) {
      return {
        title: isEdit ? hdEditor.TITLE_EDIT : hdEditor.TITLE_NEW,
        subtitle: isEdit ? hdEditor.SUBTITLE_EDIT : hdEditor.SUBTITLE_NEW,
        badge: hdEditor.BADGE
      };
    }
    if (isNurse) {
      return {
        title: isEdit ? 'Examen médical — modification' : 'Examen médical — nouvelle consultation',
        subtitle: isEdit
          ? 'Ajustez le compte rendu clinique du passage infirmerie.'
          : 'Après les constantes : symptômes, diagnostic et notes du même passage.'
      };
    }
    if (isDentist) {
      return {
        title: isEdit ? 'Modifier la consultation' : 'Nouvelle consultation',
        subtitle: isEdit
          ? 'Actualisez la date et le contenu clinique (filière dentaire).'
          : 'Identifiez l’adhérent ou un patient hors fiche, puis documentez l’examen / l’acte.'
      };
    }
    return {
      title: isEdit ? 'Modifier la consultation' : 'Nouvelle consultation',
      subtitle: isEdit
        ? 'Actualisez la date et le contenu clinique enregistré.'
        : 'Identifiez l’adhérent (matricule) ou un patient hors fiche SMIE, puis saisissez l’examen.',
      badge: 'Médecine générale'
    };
  }, [isEdit, isNurse, isDentist, isHeadDoctor, hdEditor]);

  useEffect(() => {
    if (consultation) {
      const hasMember = consultation.member_id != null;
      const { diseases, notes: diagnosisNotes } = parseDiseasesFromConsultation(consultation);
      setLegacyMode(!hasMember);
      setSelectedDiseases(useCodedDiagnosis ? diseases : []);
      setSelectedMember(
        hasMember
          ? {
              id: consultation.member_id,
              matricule: consultation.member_matricule || '',
              displayLabel: consultation.patient_name || '',
              company_name: consultation.member_company_name || ''
            }
          : null
      );
      setMatriculeQuery('');
      setSearchResults([]);
      setFormData({
        patient_name: consultation.patient_name || '',
        patient_phone: consultation.patient_phone || '',
        patient_age: consultation.patient_age ?? '',
        patient_gender: consultation.patient_gender || '',
        consultation_date: consultation.consultation_date
          ? toDateTimeLocalValue(consultation.consultation_date)
          : nowDateTimeLocalValue(),
        symptoms: consultation.symptoms || '',
        diagnosis: useCodedDiagnosis ? diagnosisNotes : consultation.diagnosis || '',
        notes: consultation.notes || '',
        weight_kg: consultation.weight_kg != null && consultation.weight_kg !== '' ? String(consultation.weight_kg) : '',
        height_cm: consultation.height_cm != null && consultation.height_cm !== '' ? String(consultation.height_cm) : '',
        bp_systolic: consultation.bp_systolic != null ? String(consultation.bp_systolic) : '',
        bp_diastolic: consultation.bp_diastolic != null ? String(consultation.bp_diastolic) : '',
        temperature_c:
          consultation.temperature_c != null && consultation.temperature_c !== ''
            ? String(consultation.temperature_c)
            : '',
        heart_rate_bpm:
          consultation.heart_rate_bpm != null ? String(consultation.heart_rate_bpm) : '',
        is_work_accident: !!consultation.is_work_accident,
        work_accident_location: consultation.work_accident_location || '',
        work_accident_severity: consultation.work_accident_severity || '',
        is_occupational_disease:
          consultation.is_occupational_disease === true ||
          consultation.is_occupational_disease === 1 ||
          consultation.is_occupational_disease === '1'
      });
    } else {
      setLegacyMode(false);
      setSelectedMember(null);
      setSelectedDiseases([]);
      setMatriculeQuery('');
      setSearchResults([]);
      setFormData({
        patient_name: '',
        patient_phone: '',
        patient_age: '',
        patient_gender: '',
        consultation_date: nowDateTimeLocalValue(),
        symptoms: '',
        diagnosis: '',
        notes: '',
        weight_kg: '',
        height_cm: '',
        bp_systolic: '',
        bp_diastolic: '',
        temperature_c: '',
        heart_rate_bpm: '',
        is_work_accident: false,
        work_accident_location: '',
        work_accident_severity: '',
        is_occupational_disease: false
      });
    }
    setError('');
  }, [consultation, useCodedDiagnosis]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchMembers = async () => {
    const q = matriculeQuery.trim();
    if (!q) {
      setError('Saisissez un matricule.');
      return;
    }
    setSearchLoading(true);
    setError('');
    try {
      const res = await lookupMembersByMatricule(q);
      if (res.success) {
        setSearchResults(res.data || []);
        if (!res.data?.length) {
          setError('Aucun adhérent actif trouvé pour ce matricule.');
        }
      }
    } catch (err) {
      setSearchResults([]);
      setError(err.message || 'Erreur de recherche');
    } finally {
      setSearchLoading(false);
    }
  };

  const pickMember = (m) => {
    setSelectedMember({
      id: m.id,
      matricule: m.matricule,
      displayLabel: `${m.prenom || ''} ${m.nom || ''}`.trim(),
      company_name: m.company_name || ''
    });
    setSearchResults([]);
    setError('');
  };

  editorStateRef.current = {
    formData,
    consultation,
    selectedMember,
    selectedDiseases,
    legacyMode,
    captureNursingVitals,
    clinicalWorkspace,
    viewerSiteId,
    useCodedDiagnosis,
    onSuccess
  };

  /**
   * @param {{ silent?: boolean }} [opts] si silent: true, n’invoque pas onSuccess (ex. enregistrement combiné avec ordonnance).
   */
  const saveConsultation = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    const complete = !!opts.complete;
    const {
      formData: fd,
      consultation: cons,
      selectedMember: member,
      selectedDiseases: diseases,
      legacyMode: leg,
      captureNursingVitals: capVit,
      clinicalWorkspace: ws,
      viewerSiteId: vsid,
      useCodedDiagnosis: codedDx,
      onSuccess: cb
    } = editorStateRef.current;

    setError('');
    setLoading(true);

    try {
      const consultationData = {
        consultation_date: fromDateTimeLocalValue(fd.consultation_date) || fd.consultation_date,
        symptoms: fd.symptoms || null,
        notes: fd.notes || null
      };

      if (ws !== 'dentist') {
        const atConfirmed =
          !!fd.is_work_accident &&
          !!fd.work_accident_location &&
          !!fd.work_accident_severity;
        if (atConfirmed) {
          consultationData.is_work_accident = true;
          consultationData.work_accident_location = fd.work_accident_location;
          consultationData.work_accident_severity = fd.work_accident_severity;
        } else {
          consultationData.is_work_accident = false;
        }
        consultationData.is_occupational_disease = !!fd.is_occupational_disease;
      }

      if (codedDx) {
        const diagPayload = buildDiagnosisApiPayload(diseases, fd.diagnosis);
        consultationData.disease_ids = diagPayload.disease_ids;
        consultationData.diagnosis_notes = diagPayload.diagnosis_notes;
      } else {
        consultationData.diagnosis = fd.diagnosis || null;
      }

      if (!cons) {
        if (leg) {
          consultationData.patient_name = fd.patient_name.trim();
          consultationData.patient_age =
            fd.patient_age !== '' ? parseInt(fd.patient_age, 10) : null;
          consultationData.patient_phone = fd.patient_phone ? fd.patient_phone.trim() : null;
          consultationData.patient_gender = fd.patient_gender || null;
          if (!consultationData.patient_name || consultationData.patient_name.length < 2) {
            throw new Error('Le nom patient est obligatoire en mode hors fiche.');
          }
        } else if (member) {
          consultationData.member_id = member.id;
        } else {
          throw new Error(
            'Choisissez un adhérent (recherche par matricule) ou passez en mode hors fiche SMIE.'
          );
        }
      }

      if (cons) {
        if (cons.member_id) {
          consultationData.member_id = cons.member_id;
        } else {
          consultationData.patient_name = fd.patient_name.trim();
          consultationData.patient_age =
            fd.patient_age !== '' ? parseInt(fd.patient_age, 10) : null;
          consultationData.patient_phone = fd.patient_phone ? fd.patient_phone.trim() : null;
          consultationData.patient_gender = fd.patient_gender || null;
          if (!consultationData.patient_name || consultationData.patient_name.length < 2) {
            throw new Error('Le nom patient est obligatoire.');
          }
        }
      }

      if (capVit) {
        const toVital = (raw, allowDecimal) => {
          const s = raw === null || raw === undefined ? '' : String(raw).trim();
          if (s === '') return null;
          const n = allowDecimal ? Number(s.replace(',', '.')) : parseInt(s, 10);
          if (Number.isNaN(n)) {
            throw new Error('Constante vitale invalide — utilisez des nombres.');
          }
          return n;
        };
        consultationData.weight_kg = toVital(fd.weight_kg, true);
        consultationData.height_cm = toVital(fd.height_cm, false);
        consultationData.bp_systolic = toVital(fd.bp_systolic, false);
        consultationData.bp_diastolic = toVital(fd.bp_diastolic, false);
        consultationData.temperature_c = toVital(fd.temperature_c, true);
        consultationData.heart_rate_bpm = toVital(fd.heart_rate_bpm, false);
      }

      if (ws === 'nurse' && !cons) {
        consultationData.service_line = 'NURSING';
      } else if (ws === 'dentist' && !cons) {
        consultationData.service_line = 'DENTAL';
      }
      const sid = parseInt(vsid, 10);
      const sitePayload = Number.isInteger(sid) && sid > 0 ? { site_id: sid } : {};

      if (cons) {
        if (complete) {
          consultationData.status = CONSULTATION_STATUS.COMPLETED;
        }
        const res = await consultationService.updateConsultation(cons.id, {
          ...consultationData,
          ...sitePayload
        });
        const updated = res.data ?? cons;
        if (cb && !silent) {
          cb({ consultation: updated, wasCreate: false, completed: complete });
        }
        return { success: true, consultation: updated, wasCreate: false, completed: complete };
      }

      const res = await consultationService.createConsultation({
        ...consultationData,
        ...sitePayload,
        status: CONSULTATION_STATUS.PENDING
      });
      if (!res.success) {
        throw new Error(res.message || 'Création impossible');
      }
      const created = res.data ?? null;
      if (cb && !silent) {
        cb({ consultation: created, wasCreate: true });
      }
      return { success: true, consultation: created, wasCreate: true };
    } catch (err) {
      const msg = err.message || 'Une erreur est survenue lors de la sauvegarde.';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({ saveConsultation }), [saveConsultation]);

  useEffect(() => {
    if (!useFixedPageFooter || typeof onFooterStateChange !== 'function') return;
    onFooterStateChange({
      formId,
      loading,
      readOnly,
      isEdit,
      isPendingConsultation: consultation?.status === CONSULTATION_STATUS.PENDING,
      formDisabled: readOnly || loading
    });
  }, [
    useFixedPageFooter,
    onFooterStateChange,
    formId,
    loading,
    readOnly,
    isEdit,
    consultation?.status
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    await saveConsultation();
  };

  const handleCompleteConsultation = async () => {
    if (readOnly) return;
    await saveConsultation({ complete: true });
  };

  const formDisabled = readOnly || loading;
  const isPendingConsultation = consultation?.status === CONSULTATION_STATUS.PENDING;

  const lockedMember = !!(consultation && consultation.member_id);
  const showLegacyFields =
    lockedMember ? false : legacyMode || (consultation && !consultation.member_id);
  const showPatientSection = !consultation;
  const showIdentityOnEdit = isEdit && !identityInPageFooter;
  const portalIdentityToFooter = isEdit && identityInPageFooter && !!identityFooterTarget;
  const hasRecordedVitals =
    formData.weight_kg !== '' ||
    formData.height_cm !== '' ||
    formData.bp_systolic !== '' ||
    formData.bp_diastolic !== '' ||
    formData.temperature_c !== '' ||
    formData.heart_rate_bpm !== '';

  const matriculeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!selectedMember && !searchLoading) handleSearchMembers();
    }
  };

  const renderIdentitySection = (placement = 'inline') => {
    const memberMatricule = consultation?.member_matricule || '';
    const memberCompany = consultation?.member_company_name || '';

    if (placement === 'footer' || placement === 'header') {
      return (
        <div
          className={`consult-modal__section consult-modal__identity consult-modal__identity--page-${placement}`}
        >
          <ConsultationPatientIdentityPremium
            variant={placement === 'footer' ? 'footer' : 'default'}
            formData={formData}
            onChange={handleChange}
            lockedMember={lockedMember || readOnly}
            memberMatricule={memberMatricule}
            memberCompany={memberCompany}
          />
        </div>
      );
    }

    const nameCol = { md: 8 };
    const phoneCol = { md: 4 };
    const ageCol = { md: 3 };
    const genderCol = { md: 4 };

    return (
    <div className="consult-modal__section consult-modal__identity mb-3">
      <div className="consult-modal__section-label">Identité saisie sur la consultation</div>
      <Row>
        <Col {...nameCol}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom affiché <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="patient_name"
              value={formData.patient_name}
              onChange={handleChange}
              required
              placeholder="Nom complet"
              disabled={lockedMember}
            />
          </Form.Group>
        </Col>
        <Col {...phoneCol}>
          <Form.Group className="mb-3">
            <Form.Label>Téléphone</Form.Label>
            <Form.Control
              type="tel"
              name="patient_phone"
              value={formData.patient_phone}
              onChange={handleChange}
              disabled={lockedMember}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col {...ageCol}>
          <Form.Group className="mb-3">
            <Form.Label>Âge</Form.Label>
            <Form.Control
              type="number"
              name="patient_age"
              value={formData.patient_age}
              onChange={handleChange}
              min="0"
              max="150"
              disabled={lockedMember}
            />
          </Form.Group>
        </Col>
        <Col {...genderCol}>
          <Form.Group className="mb-0">
            <Form.Label>Genre</Form.Label>
            <Form.Select
              name="patient_gender"
              value={formData.patient_gender}
              onChange={handleChange}
              disabled={lockedMember}
            >
              <option value="">—</option>
              <option value="M">M</option>
              <option value="F">F</option>
              <option value="OTHER">Autre</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      {lockedMember && consultation ? (
        <div className="consult-modal__identity-member small text-muted font-monospace mt-2 mb-0">
          {memberMatricule}
          {memberCompany ? ` · ${memberCompany}` : ''}
        </div>
      ) : null}
    </div>
    );
  };

  return (
    <div className={`consult-editor-inner${useFixedPageFooter ? ' consult-editor-inner--external-footer' : ''}`}>
      {portalIdentityToFooter
        ? createPortal(renderIdentitySection('footer'), identityFooterTarget)
        : null}
      {!isEdit && (
        <div className="consult-editor-page__title-bar">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <h1 className="h4 mb-0">{headerMeta.title}</h1>
            <Badge
              pill
              bg={isNurse ? 'success' : isDentist ? 'info' : isHeadDoctor ? 'dark' : 'primary'}
              className="fw-normal"
            >
              {headerMeta.badge ||
                (isNurse ? 'Infirmerie' : isDentist ? 'Filière dentaire' : 'Médecine générale')}
            </Badge>
          </div>
          <div className="consult-modal__subtitle text-muted">{headerMeta.subtitle}</div>
        </div>
      )}

      <Form id={formId} onSubmit={handleSubmit}>
        <fieldset disabled={readOnly} className={readOnly ? 'consult-editor-fieldset--readonly' : undefined}>
        <div className="consult-editor-page__body">
          {readOnly ? (
            <Alert variant="light" className="consult-editor-closed-notice py-2 small mb-3 border">
              <strong>Dossier clôturé.</strong> Consultation en lecture seule — vous pouvez consulter le contenu et
              imprimer les actes (ordonnance, examens, certificats).
            </Alert>
          ) : null}
          {error && (
            <Alert variant="danger" className="py-2 small mb-3" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {showIdentityOnEdit ? renderIdentitySection() : null}

          {isNurse && (
            <div className="consult-modal__workflow-hint" role="note">
              <span className="consult-modal__hint-icon" aria-hidden>
                <Icon name="infoCircle" size={18} />
              </span>
              <span>
                <strong>Diagnostic</strong> = code(s) maladie du référentiel (recherche par code ou
                libellé). Complétez symptômes, codes et notes du passage.
              </span>
            </div>
          )}
          {!isNurse && captureNursingVitals && (
            <div className="consult-modal__workflow-hint" role="note">
              <span className="consult-modal__hint-icon" aria-hidden>
                <Icon name="infoCircle" size={18} />
              </span>
              <span>
                {isHeadDoctor ? (
                  hdEditor.DIRECT_VITALS_HINT
                ) : (
                  <>
                    Parcours direct médecin : vous pouvez renseigner les{' '}
                    <strong>constantes vitales</strong> et l&apos;<strong>examen clinique</strong> dans la
                    même prise en charge.
                  </>
                )}
              </span>
            </div>
          )}

          {showPatientSection && (
            <div className="consult-modal__section">
              <div className="consult-modal__section-label">Identification du patient</div>

              <ButtonGroup className="consult-modal__toggle w-100 mb-3" aria-label="Mode patient">
                <Button
                  type="button"
                  variant={!legacyMode ? 'primary' : 'outline-primary'}
                  className="flex-fill"
                  onClick={() => {
                    setLegacyMode(false);
                    setError('');
                  }}
                >
                  Fiche adhérent (matricule)
                </Button>
                <Button
                  type="button"
                  variant={legacyMode ? 'secondary' : 'outline-secondary'}
                  className="flex-fill"
                  onClick={() => {
                    setLegacyMode(true);
                    setSelectedMember(null);
                    setSearchResults([]);
                    setError('');
                  }}
                >
                  Hors fiche SMIE
                </Button>
              </ButtonGroup>

              {!legacyMode ? (
                <>
                  <Row className="g-2 align-items-end">
                    <Col>
                      <Form.Label>Matricule</Form.Label>
                      <Form.Control
                        value={matriculeQuery}
                        onChange={(ev) => setMatriculeQuery(ev.target.value)}
                        onKeyDown={matriculeKeyDown}
                        placeholder="Saisie exacte carte adhérent"
                        disabled={!!selectedMember}
                        autoComplete="off"
                        aria-describedby="consult-matricule-hint"
                      />
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant="primary"
                        type="button"
                        className="px-3"
                        onClick={handleSearchMembers}
                        disabled={searchLoading || !!selectedMember}
                      >
                        {searchLoading ? (
                          <Spinner animation="border" size="sm" role="status" />
                        ) : (
                          'Rechercher'
                        )}
                      </Button>
                    </Col>
                  </Row>
                  <Form.Text id="consult-matricule-hint" muted className="small d-block mt-1">
                    Entrée ou bouton pour lancer la recherche.
                  </Form.Text>

                  {selectedMember && (
                    <div className="consult-modal__patient-chip p-3 mt-3 rounded">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                          <div className="small text-muted text-uppercase fw-semibold" style={{ fontSize: '0.65rem' }}>
                            Adhérent sélectionné
                          </div>
                          <div className="fw-semibold mt-1">{selectedMember.displayLabel || '—'}</div>
                          <div className="small text-muted font-monospace mt-1">
                            {selectedMember.matricule}
                            {selectedMember.company_name ? ` · ${selectedMember.company_name}` : ''}
                          </div>
                        </div>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => setSelectedMember(null)}>
                          Changer
                        </Button>
                      </div>
                    </div>
                  )}

                  {searchResults.length > 0 && !selectedMember && (
                    <div className="mt-3">
                      <Form.Label className="small text-muted">Résultats — sélectionnez une ligne</Form.Label>
                      <ListGroup variant="flush" className="consult-modal__list-hits border rounded">
                        {searchResults.map((m) => (
                          <ListGroup.Item
                            key={m.id}
                            action
                            as="button"
                            type="button"
                            className="d-flex justify-content-between align-items-center gap-2 text-start"
                            onClick={() => pickMember(m)}
                          >
                            <span>
                              <span className="fw-medium">
                                {m.prenom} {m.nom}
                              </span>
                              <span className="text-muted small ms-2 font-monospace">{m.matricule}</span>
                              {m.company_name && (
                                <span className="d-block small text-muted mt-1">{m.company_name}</span>
                              )}
                            </span>
                            <Badge bg="primary" className="flex-shrink-0">
                              Choisir
                            </Badge>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted small mb-0">
                  Mode exceptionnel : pas de lien vers la base adhérents. Renseignez le nom ci-dessous
                  (obligatoire) et les champs facultatifs.
                </p>
              )}
            </div>
          )}

          {!isEdit && showLegacyFields ? renderIdentitySection() : null}

          {captureNursingVitals && (
            <div className="consult-modal__vitals mb-3">
              <div className="consult-modal__vitals-head">
                Constantes vitales sur ce passage
                <small className="consult-modal__vitals-desc">
                  Ces mesures restent attachées à la consultation et figurent dans l’historique dossier.
                </small>
              </div>
              <div className="consult-modal__vitals-body">
                <Row className="g-2">
                  <Col md={4} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Poids (kg)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        name="weight_kg"
                        value={formData.weight_kg}
                        onChange={handleChange}
                        placeholder="ex. 72,5"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Taille (cm)</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        name="height_cm"
                        value={formData.height_cm}
                        onChange={handleChange}
                        placeholder="ex. 175"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Température (°C)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="temperature_c"
                        value={formData.temperature_c}
                        onChange={handleChange}
                        placeholder="ex. 37,2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>TA systolique</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        name="bp_systolic"
                        value={formData.bp_systolic}
                        onChange={handleChange}
                        placeholder="120"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>TA diastolique</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        name="bp_diastolic"
                        value={formData.bp_diastolic}
                        onChange={handleChange}
                        placeholder="80"
                      />
                      <Form.Text muted className="small">
                        mmHg (systolique / diastolique)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group className="mb-0">
                      <Form.Label>Fréquence cardiaque (bpm)</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        name="heart_rate_bpm"
                        value={formData.heart_rate_bpm}
                        onChange={handleChange}
                        placeholder="ex. 72"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {!captureNursingVitals && hasRecordedVitals && (
            <div className="consult-modal__section mb-3">
              <div className="consult-modal__section-label">Constantes vitales relevées en amont</div>
              <Row className="g-2">
                <Col md={4} sm={6}>
                  <div className="small text-muted">Poids</div>
                  <div className="fw-semibold">{formData.weight_kg || '—'} {formData.weight_kg ? 'kg' : ''}</div>
                </Col>
                <Col md={4} sm={6}>
                  <div className="small text-muted">Taille</div>
                  <div className="fw-semibold">{formData.height_cm || '—'} {formData.height_cm ? 'cm' : ''}</div>
                </Col>
                <Col md={4} sm={6}>
                  <div className="small text-muted">Température</div>
                  <div className="fw-semibold">
                    {formData.temperature_c || '—'} {formData.temperature_c ? '°C' : ''}
                  </div>
                </Col>
                <Col md={4} sm={6}>
                  <div className="small text-muted">TA</div>
                  <div className="fw-semibold">
                    {formData.bp_systolic && formData.bp_diastolic
                      ? `${formData.bp_systolic}/${formData.bp_diastolic}`
                      : '—'}
                  </div>
                </Col>
                <Col md={4} sm={6}>
                  <div className="small text-muted">Fréquence cardiaque</div>
                  <div className="fw-semibold">
                    {formData.heart_rate_bpm || '—'} {formData.heart_rate_bpm ? 'bpm' : ''}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {showWorkAccidentContext ? (
            <div
              className={`consult-modal__section consult-wa-section${
                workAccidentIsConfirmed ? ' consult-wa-section--active' : ''
              }`}
            >
              <div className="consult-wa-field">
                <div className="consult-wa-field__copy">
                  <span className="consult-wa-field__label" id={`${formId}-work-accident-label`}>
                    {workAccidentCopy.label || workAccidentCopy.LABEL}
                  </span>
                  <span className="consult-wa-field__hint">
                    {workAccidentCopy.hint || workAccidentCopy.HINT}
                  </span>
                </div>

                <div
                  className={`consult-wa-segment${workAccidentIsConfirmed ? ' is-yes' : ' is-no'}`}
                  role="radiogroup"
                  aria-labelledby={`${formId}-work-accident-label`}
                >
                  <span className="consult-wa-segment__glider" aria-hidden />

                  <label className="consult-wa-segment__option">
                    <input
                      type="radio"
                      className="consult-wa-segment__input"
                      name={`${formId}-work-accident`}
                      value="no"
                      checked={!workAccidentIsConfirmed}
                      disabled={readOnly || loading}
                      onChange={clearWorkAccident}
                    />
                    <span className="consult-wa-segment__pill">
                      {workAccidentCopy.no || workAccidentCopy.NO}
                    </span>
                  </label>

                  <label className="consult-wa-segment__option consult-wa-segment__option--yes">
                    <input
                      type="radio"
                      className="consult-wa-segment__input"
                      name={`${formId}-work-accident`}
                      value="yes"
                      checked={workAccidentIsConfirmed}
                      disabled={readOnly || loading}
                      onChange={openWorkAccidentModal}
                    />
                    <span className="consult-wa-segment__pill">
                      {workAccidentCopy.yes || workAccidentCopy.YES}
                    </span>
                  </label>
                </div>
              </div>

              {workAccidentIsConfirmed ? (
                <div className="consult-wa-summary">
                  <div className="consult-wa-summary__chips">
                    <span className="consult-wa-summary__chip consult-wa-summary__chip--location">
                      {getWorkAccidentLocationLabel(formData.work_accident_location)}
                    </span>
                    <span className="consult-wa-summary__chip consult-wa-summary__chip--severity">
                      {formData.work_accident_severity}
                    </span>
                  </div>
                  {!readOnly ? (
                    <button
                      type="button"
                      className="consult-wa-summary__edit"
                      onClick={openWorkAccidentModal}
                      disabled={loading}
                    >
                      {workAccidentCopy.summaryEdit || workAccidentCopy.SUMMARY_EDIT || 'Modifier'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {showWorkAccidentContext ? (
            <div
              className={`consult-modal__section consult-wa-section${
                formData.is_occupational_disease ? ' consult-wa-section--active' : ''
              }`}
            >
              <div className="consult-wa-field">
                <div className="consult-wa-field__copy">
                  <span className="consult-wa-field__label" id={`${formId}-occupational-disease-label`}>
                    {occupationalDiseaseCopy.label || occupationalDiseaseCopy.LABEL}
                  </span>
                  <span className="consult-wa-field__hint">
                    {occupationalDiseaseCopy.hint || occupationalDiseaseCopy.HINT}
                  </span>
                </div>

                <div
                  className={`consult-wa-segment${formData.is_occupational_disease ? ' is-yes' : ' is-no'}`}
                  role="radiogroup"
                  aria-labelledby={`${formId}-occupational-disease-label`}
                >
                  <span className="consult-wa-segment__glider" aria-hidden />

                  <label className="consult-wa-segment__option">
                    <input
                      type="radio"
                      className="consult-wa-segment__input"
                      name={`${formId}-occupational-disease`}
                      value="no"
                      checked={!formData.is_occupational_disease}
                      disabled={readOnly || loading}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          is_occupational_disease: false
                        }))
                      }
                    />
                    <span className="consult-wa-segment__pill">
                      {occupationalDiseaseCopy.no || occupationalDiseaseCopy.NO}
                    </span>
                  </label>

                  <label className="consult-wa-segment__option consult-wa-segment__option--yes">
                    <input
                      type="radio"
                      className="consult-wa-segment__input"
                      name={`${formId}-occupational-disease`}
                      value="yes"
                      checked={formData.is_occupational_disease}
                      disabled={readOnly || loading}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          is_occupational_disease: true
                        }))
                      }
                    />
                    <span className="consult-wa-segment__pill">
                      {occupationalDiseaseCopy.yes || occupationalDiseaseCopy.YES}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          ) : null}

            <div className="consult-modal__section">
              <div className="consult-modal__section-label">Examen & suivi</div>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date et heure de consultation</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="consultation_date"
                    value={formData.consultation_date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Symptômes</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="symptoms"
                value={formData.symptoms}
                onChange={handleChange}
                placeholder="Motif, plaintes, antécédents pertinents…"
              />
            </Form.Group>
            {useCodedDiagnosis ? (
              <div className="mb-3">
                <DiagnosisDiseaseField
                  selectedDiseases={selectedDiseases}
                  onDiseasesChange={setSelectedDiseases}
                  notes={formData.diagnosis}
                  onNotesChange={(value) =>
                    setFormData((prev) => ({ ...prev, diagnosis: value }))
                  }
                  disabled={loading}
                />
              </div>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Diagnostic</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleChange}
                  placeholder="Hypothèse ou diagnostic retenu…"
                />
              </Form.Group>
            )}
            <Form.Group className="mb-0">
              <Form.Label>Notes complémentaires</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Orientation, conseils, suivi…"
              />
            </Form.Group>
          </div>

          {afterExamenSection ? afterExamenSection : null}
        </div>

        {!useFixedPageFooter ? (
        <div className="consult-editor-page__footer d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <ClinicalButton variant="secondary" size="md" type="button" onClick={onCancel} disabled={loading}>
            {readOnly ? 'Retour à la liste' : 'Annuler · retour liste'}
          </ClinicalButton>
          {!readOnly ? (
            <div className="d-flex flex-wrap gap-2">
              {isPendingConsultation && isEdit ? (
                <ClinicalButton
                  variant="secondary"
                  size="md"
                  type="button"
                  loading={loading}
                  disabled={formDisabled}
                  onClick={() => saveConsultation()}
                >
                  Enregistrer le brouillon
                </ClinicalButton>
              ) : null}
              <ClinicalButton
                variant="primary"
                size="md"
                type={isPendingConsultation && isEdit ? 'button' : 'submit'}
                loading={loading}
                disabled={formDisabled}
                onClick={
                  isPendingConsultation && isEdit
                    ? () => handleCompleteConsultation()
                    : undefined
                }
              >
                {isEdit
                  ? isPendingConsultation
                    ? 'Terminer la consultation'
                    : 'Enregistrer les modifications'
                  : 'Créer la consultation'}
              </ClinicalButton>
            </div>
          ) : null}
        </div>
        ) : null}
        </fieldset>
      </Form>

      {showWorkAccidentContext ? (
        <WorkAccidentConfirmModal
          show={showWorkAccidentModal}
          onHide={closeWorkAccidentModal}
          onConfirm={confirmWorkAccident}
          copy={workAccidentCopy}
          initialLocation={formData.work_accident_location}
          initialSeverity={formData.work_accident_severity}
          disabled={readOnly || loading}
          tone={clinicalWorkspace}
        />
      ) : null}
    </div>
  );
});

export default ConsultationEditor;
